import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './QRMenu.css';
import { API_CONFIG, API_ENDPOINTS } from '../config/apiRouting';
import { useCart } from '../contexts/CartContext';
import ShoppingCart from './ShoppingCart';

// Public API endpoints (no authentication required for customer-facing menu)
const PUBLIC_API = {
  // Use Lambda URLs directly for public access (no auth required for store lookup)
  stores: API_CONFIG.microservices.stores.url,
  menuItems: API_CONFIG.microservices.menuManagement.url
};

interface MenuItem {
  menuItemId: string;
  name: string;
  description: string;
  basePrice: number;
  price: number;
  effectivePrice?: number;
  category: string;
  imageUrl?: string;
  nutritionInfo?: {
    calories?: number;
    fat?: number;
    protein?: number;
    carbohydrates?: number;
    sodium?: number;
    fiber?: number;
    sugar?: number;
  };
  allergens?: string[];
  dietaryTags?: string[];
  isAvailable: boolean;
  preparationTime?: string;
}

interface Store {
  storeId: string;
  name: string;
  storeCode?: string;
  code?: string; // For backward compatibility
  address?: string;
  phone?: string;
  hours?: string;
  enterpriseId: string;
}

const QRMenu: React.FC = () => {
  const { storeCode } = useParams<{ storeCode: string }>();
  const [store, setStore] = useState<Store | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState<string | null>(null);

  const { addItem, setStore: setCartStore, itemCount, openCart } = useCart();

  useEffect(() => {
    if (storeCode) {
      loadStoreAndMenu();
    }
  }, [storeCode]);

  const loadStoreAndMenu = async () => {
    try {
      setLoading(true);

      // 1. Find store by code - use public Lambda URL (no auth required for customer menu)
      console.log('[QRMenu] Looking up store by code:', storeCode);
      const storesResponse = await fetch(`${PUBLIC_API.stores}/api/stores/public/code/${storeCode}`);

      // If public endpoint doesn't exist, fall back to scanning all stores
      let foundStore: Store | null = null;

      if (storesResponse.ok) {
        const storeData = await storesResponse.json();
        foundStore = storeData.store || storeData;
        console.log('[QRMenu] Store found via public endpoint:', foundStore);
      } else {
        // Fallback: Try public stores list endpoint
        console.log('[QRMenu] Public endpoint not found, trying stores list...');
        const allStoresResponse = await fetch(`${PUBLIC_API.stores}/api/stores/public`);

        if (allStoresResponse.ok) {
          const allStoresData = await allStoresResponse.json();
          const stores = Array.isArray(allStoresData) ? allStoresData : (allStoresData.data || allStoresData.stores || []);
          foundStore = stores.find((s: any) =>
            s.storeCode === storeCode || s.code === storeCode
          );
        }
      }

      if (!foundStore) {
        console.error('[QRMenu] Store not found for code:', storeCode);
        setError(`Store not found for code: ${storeCode}`);
        return;
      }

      console.log('[QRMenu] Store found:', foundStore.name, 'Enterprise:', foundStore.enterpriseId);
      setStore(foundStore);

      // Set cart store context
      setCartStore({
        storeId: foundStore.storeId,
        storeCode: foundStore.storeCode || foundStore.code || storeCode || '',
        storeName: foundStore.name,
        enterpriseId: foundStore.enterpriseId
      });

      // 2. Load menu items for the store's enterprise - use public endpoint
      const menuResponse = await fetch(`${PUBLIC_API.menuItems}/api/menu-items/public?enterpriseId=${foundStore.enterpriseId}`);

      // Fallback to regular endpoint if public doesn't exist
      if (!menuResponse.ok) {
        console.log('[QRMenu] Public menu endpoint not available, trying standard endpoint...');
        const fallbackResponse = await fetch(`${PUBLIC_API.menuItems}/api/menu-items?enterpriseId=${foundStore.enterpriseId}`);
        if (!fallbackResponse.ok) throw new Error('Failed to load menu items');
        const menuData = await fallbackResponse.json();
        var items = Array.isArray(menuData) ? menuData : (menuData.data || []);
      } else {
        const menuData = await menuResponse.json();
        var items = Array.isArray(menuData) ? menuData : (menuData.data || []);
      }

      // 3. Filter items assigned to this store (if menu assignment system is active)
      // For now, we'll show all enterprise items. Later we can add store-specific filtering.
      
      // 4. Get effective pricing for each item
      const itemsWithPricing = await Promise.all(
        items.map(async (item: MenuItem) => {
          try {
            const pricingResponse = await fetch(
              API_ENDPOINTS.pricingResolve(item.menuItemId, foundStore!.storeId, foundStore!.enterpriseId)
            );
            
            if (pricingResponse.ok) {
              const pricingData = await pricingResponse.json();
              return {
                ...item,
                effectivePrice: pricingData.data.effectivePrice
              };
            } else {
              return {
                ...item,
                effectivePrice: item.basePrice || item.price
              };
            }
          } catch (error) {
            console.warn(`Pricing lookup failed for ${item.name}:`, error);
            return {
              ...item,
              effectivePrice: item.basePrice || item.price
            };
          }
        })
      );

      setMenuItems(itemsWithPricing);

      // 5. Extract unique categories
      const uniqueCategories = Array.from(new Set(itemsWithPricing.map(item => item.category))).sort();
      setCategories(uniqueCategories);
      
      if (uniqueCategories.length > 0) {
        setSelectedCategory(uniqueCategories[0]);
      }

    } catch (error) {
      console.error('Error loading store menu:', error);
      setError('Failed to load menu. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = selectedCategory 
    ? menuItems.filter(item => item.category === selectedCategory)
    : menuItems;

  const openItemDetails = (item: MenuItem) => {
    setSelectedItem(item);
  };

  const closeItemDetails = () => {
    setSelectedItem(null);
    setItemQuantity(1);
  };

  const handleAddToCart = (item: MenuItem) => {
    const price = item.effectivePrice || item.basePrice || item.price;
    addItem({
      menuItemId: item.menuItemId,
      name: item.name,
      price: price,
      imageUrl: item.imageUrl
    }, itemQuantity);

    // Show feedback
    setAddedToCart(item.menuItemId);
    setTimeout(() => setAddedToCart(null), 1500);

    closeItemDetails();
  };

  const handleQuickAdd = (item: MenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const price = item.effectivePrice || item.basePrice || item.price;
    addItem({
      menuItemId: item.menuItemId,
      name: item.name,
      price: price,
      imageUrl: item.imageUrl
    }, 1);

    // Show feedback
    setAddedToCart(item.menuItemId);
    setTimeout(() => setAddedToCart(null), 1500);
  };

  if (loading) {
    return (
      <div className="qr-menu-loading">
        <div className="loading-spinner"></div>
        <p>Loading menu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qr-menu-error">
        <h2>Menu Unavailable</h2>
        <p>{error}</p>
        <p>Please scan a valid QR code or contact the restaurant.</p>
      </div>
    );
  }

  return (
    <div className="qr-menu">
      {/* Header */}
      <header className="qr-menu-header">
        <div className="store-info">
          <h1>{store?.name}</h1>
          {store?.address && <p className="store-address">{store.address}</p>}
          {store?.phone && <p className="store-phone">{store.phone}</p>}
          {store?.hours && <p className="store-hours">{store.hours}</p>}
          <div className="menu-url">
            <p className="url-label">Share this menu:</p>
            <div className="url-container">
              <code className="menu-url-display">{window.location.href}</code>
              <button 
                className="copy-url-btn"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    setUrlCopied(true);
                    setTimeout(() => setUrlCopied(false), 2000);
                  }).catch(() => {
                    // Fallback for browsers that don't support clipboard API
                    const textArea = document.createElement('textarea');
                    textArea.value = window.location.href;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    setUrlCopied(true);
                    setTimeout(() => setUrlCopied(false), 2000);
                  });
                }}
                title={urlCopied ? "Copied!" : "Copy menu URL"}
              >
                {urlCopied ? '[COPIED]' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
        <div className="menu-title">
          <h2>Our Menu</h2>
          <p>Tap any item for details</p>
        </div>

        {/* Cart Button */}
        <button className="cart-fab" onClick={openCart}>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
        </button>
      </header>

      {/* Category Tabs */}
      <nav className="category-nav">
        <div className="category-tabs">
          {categories.map(category => (
            <button
              key={category}
              className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </nav>

      {/* Menu Items */}
      <main className="menu-content">
        <div className="menu-items">
          {filteredItems.map(item => (
            <div
              key={item.menuItemId}
              className="menu-item-card"
              onClick={() => openItemDetails(item)}
            >
              {item.imageUrl && (
                <div className="item-image">
                  <img src={item.imageUrl} alt={item.name} />
                </div>
              )}
              <div className="item-info">
                <h3 className="item-name">{item.name}</h3>
                <p className="item-description">{item.description}</p>
                <div className="item-meta">
                  <span className="item-price">
                    ${(item.effectivePrice || item.basePrice || item.price).toFixed(2)}
                  </span>
                  {item.preparationTime && (
                    <span className="prep-time">{item.preparationTime}</span>
                  )}
                </div>
                {item.dietaryTags && item.dietaryTags.length > 0 && (
                  <div className="dietary-tags">
                    {item.dietaryTags.map(tag => (
                      <span key={tag} className="dietary-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="item-action">
                <button
                  className={`quick-add-btn ${addedToCart === item.menuItemId ? 'added' : ''}`}
                  onClick={(e) => handleQuickAdd(item, e)}
                  disabled={!item.isAvailable}
                >
                  {addedToCart === item.menuItemId ? 'Added!' : '+'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Item Details Modal */}
      {selectedItem && (
        <div className="item-modal-overlay" onClick={closeItemDetails}>
          <div className="item-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeItemDetails}>×</button>
            
            {selectedItem.imageUrl && (
              <div className="modal-image">
                <img src={selectedItem.imageUrl} alt={selectedItem.name} />
              </div>
            )}
            
            <div className="modal-content">
              <h2>{selectedItem.name}</h2>
              <p className="modal-description">{selectedItem.description}</p>
              
              <div className="price-section">
                <span className="modal-price">
                  ${(selectedItem.effectivePrice || selectedItem.basePrice || selectedItem.price).toFixed(2)}
                </span>
                {selectedItem.preparationTime && (
                  <span className="modal-prep-time">Prep time: {selectedItem.preparationTime}</span>
                )}
              </div>

              {/* Nutrition Information */}
              {selectedItem.nutritionInfo && (
                <div className="nutrition-section">
                  <h3>Nutrition Facts</h3>
                  <div className="nutrition-grid">
                    {selectedItem.nutritionInfo.calories && (
                      <div className="nutrition-item">
                        <span className="nutrition-label">Calories</span>
                        <span className="nutrition-value">{selectedItem.nutritionInfo.calories}</span>
                      </div>
                    )}
                    {selectedItem.nutritionInfo.fat && (
                      <div className="nutrition-item">
                        <span className="nutrition-label">Fat (g)</span>
                        <span className="nutrition-value">{selectedItem.nutritionInfo.fat}</span>
                      </div>
                    )}
                    {selectedItem.nutritionInfo.protein && (
                      <div className="nutrition-item">
                        <span className="nutrition-label">Protein (g)</span>
                        <span className="nutrition-value">{selectedItem.nutritionInfo.protein}</span>
                      </div>
                    )}
                    {selectedItem.nutritionInfo.carbohydrates && (
                      <div className="nutrition-item">
                        <span className="nutrition-label">Carbs (g)</span>
                        <span className="nutrition-value">{selectedItem.nutritionInfo.carbohydrates}</span>
                      </div>
                    )}
                    {selectedItem.nutritionInfo.sodium && (
                      <div className="nutrition-item">
                        <span className="nutrition-label">Sodium (mg)</span>
                        <span className="nutrition-value">{selectedItem.nutritionInfo.sodium}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Allergen Information */}
              {selectedItem.allergens && selectedItem.allergens.length > 0 && (
                <div className="allergen-section">
                  <h3>Contains Allergens</h3>
                  <div className="allergen-list">
                    {selectedItem.allergens.map(allergen => (
                      <span key={allergen} className="allergen-tag">{allergen}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Dietary Tags */}
              {selectedItem.dietaryTags && selectedItem.dietaryTags.length > 0 && (
                <div className="dietary-section">
                  <h3>Dietary Information</h3>
                  <div className="dietary-list">
                    {selectedItem.dietaryTags.map(tag => (
                      <span key={tag} className="dietary-tag-modal">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Add to Cart Section */}
              <div className="add-to-cart-section">
                <div className="quantity-selector">
                  <button
                    className="qty-btn"
                    onClick={() => setItemQuantity(Math.max(1, itemQuantity - 1))}
                  >
                    -
                  </button>
                  <span className="qty-value">{itemQuantity}</span>
                  <button
                    className="qty-btn"
                    onClick={() => setItemQuantity(itemQuantity + 1)}
                  >
                    +
                  </button>
                </div>
                <button
                  className="add-to-cart-btn"
                  onClick={() => handleAddToCart(selectedItem)}
                  disabled={!selectedItem.isAvailable}
                >
                  {selectedItem.isAvailable
                    ? `Add to Order - $${((selectedItem.effectivePrice || selectedItem.basePrice || selectedItem.price) * itemQuantity).toFixed(2)}`
                    : 'Currently Unavailable'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shopping Cart Drawer */}
      <ShoppingCart />

      {/* Footer */}
      <footer className="qr-menu-footer">
        <p>Powered by BigSystemOne</p>
        <p>Prices and availability subject to change</p>
      </footer>
    </div>
  );
};

export default QRMenu;